import React, { useEffect, useRef, useState } from 'react';
import { FiDownload, FiMail, FiSend, FiExternalLink } from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import { emailQuote, pdfUrl, inr, fmtDate, errMsg } from './quoteApi';

const isMobile = () => /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

const fetchPdfBlob = async (q) => {
  const r = await fetch(`${pdfUrl(q)}?download=1`);
  if (!r.ok) throw new Error('PDF download failed');
  return r.blob();
};

const summaryText = (q) => {
  const d = q.data;
  return `*Royal Garden Villas — Quotation ${q.quote_no}*\n` +
    `${d.villa.bhk_name}\n${fmtDate(d.check_in)} → ${fmtDate(d.check_out)} · ${d.nights} night${d.nights !== 1 ? 's' : ''} · ${d.guests} guests\n` +
    `Grand Total (incl. refundable deposit): *${inr(d.grand_total)}*\nValid until ${fmtDate(d.valid_until)}\n\n` +
    `View / download PDF: ${pdfUrl(q)}`;
};

export const QuoteShareActions = ({ quote, waPhone }) => {
  const [email, setEmail] = useState(quote.data.client_email || '');
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState(null);
  const fname = `Quotation_${quote.quote_no}_Royal_Garden_Villas.pdf`;
  const blobRef = useRef(null);
  useEffect(() => { if (isMobile()) fetchPdfBlob(quote).then((b) => { blobRef.current = b; }).catch(() => {}); }, [quote]);
  const openWa = () => {
    const num = (waPhone || '').replace(/\D/g, '');
    const target = num ? (num.length === 10 ? `91${num}` : num) : '';
    window.open(`https://wa.me/${target}?text=${encodeURIComponent(summaryText(quote))}`, '_blank', 'noopener');
  };

  const download = async () => {
    setBusy('dl'); setMsg(null);
    try {
      const blob = await fetchPdfBlob(quote);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = fname; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch {
      window.open(pdfUrl(quote), '_blank', 'noopener');
    } finally { setBusy(''); }
  };

  const whatsapp = async () => {
    setMsg(null);
    const blob = blobRef.current;
    if (blob && navigator.canShare) {
      const file = new File([blob], fname, { type: 'application/pdf' });
      if (navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file], title: `Quotation ${quote.quote_no}`, text: summaryText(quote) });
        } catch (e) {
          if (e?.name !== 'AbortError') openWa();
        }
        return;
      }
    }
    openWa();
  };

  const sendEmail = async () => {
    setBusy('em'); setMsg(null);
    try {
      await emailQuote(quote.id, email.trim());
      setMsg({ ok: true, t: `Quotation emailed to ${email.trim()}` });
    } catch (e) {
      setMsg({ ok: false, t: errMsg(e, 'Email failed') });
    } finally { setBusy(''); }
  };

  const btn = 'flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-semibold transition-colors disabled:opacity-60';
  return (
    <div data-testid="quote-share-actions" className="space-y-3">
      <div className="flex flex-col sm:flex-row gap-2">
        <button data-testid="quote-download-btn" onClick={download} disabled={!!busy} className={`${btn} bg-[#1A1A2E] text-white hover:bg-[#2A2A44]`}>
          <FiDownload className="w-4 h-4" /> {busy === 'dl' ? 'Preparing…' : 'Download PDF'}
        </button>
        <button data-testid="quote-whatsapp-btn" onClick={whatsapp} disabled={!!busy} className={`${btn} bg-[#25D366] text-white hover:bg-[#1EBE5A]`}>
          <FaWhatsapp className="w-4 h-4" /> Share on WhatsApp
        </button>
        <a data-testid="quote-view-btn" href={pdfUrl(quote)} target="_blank" rel="noopener noreferrer" className={`${btn} sm:flex-none border border-gray-200 text-[#1A1A2E] hover:border-[#F57C20]`}>
          <FiExternalLink className="w-4 h-4" /> View
        </a>
      </div>
      <div className="flex gap-2">
        <div className="relative flex-1">
          <FiMail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
          <input data-testid="quote-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email address"
            className="w-full bg-[#F7F8FA] border border-gray-200 rounded-lg pl-9 pr-3 py-3 text-sm focus:outline-none focus:border-[#F57C20]" />
        </div>
        <button data-testid="quote-email-send-btn" onClick={sendEmail} disabled={!!busy || !email.trim()} className={`${btn} flex-none bg-[#F57C20] text-white hover:bg-[#E06A10]`}>
          <FiSend className="w-4 h-4" /> {busy === 'em' ? 'Sending…' : 'Email'}
        </button>
      </div>
      {msg && <p data-testid="quote-share-msg" className={`text-xs ${msg.ok ? 'text-green-600' : 'text-red-600'}`}>{msg.t}</p>}
    </div>
  );
};

export default QuoteShareActions;
